import React from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { Shield, Database, Lock, Eye, UserCheck, Clock, Share2, Mail, FileText } from 'lucide-react';

export const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen py-12 md:py-16 bg-[#F9F8F6]" data-testid="privacy-policy-page">
      <SEO
        title="Privacy Policy"
        description="Learn how Vintage Wardrobe Co collects, uses, and protects your personal data under UK GDPR."
        path="/privacy-policy"
      />
      <div className="container-custom max-w-3xl">
        {/* Header */}
        <div className="mb-10 md:mb-14">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-charcoal flex items-center justify-center">
              <Shield className="w-6 h-6 text-[#C9A84C]" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-heading font-bold" data-testid="privacy-title">Privacy Policy</h1>
            </div>
          </div>
          <p className="text-softgrey text-sm md:text-base">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </div>

        <div className="space-y-10">
          {/* Who We Are */}
          <section data-testid="who-we-are-section">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-5 h-5 text-[#C9A84C]" />
              <h2 className="text-xl md:text-2xl font-heading font-bold">Who We Are</h2>
            </div>
            <p className="text-softgrey leading-relaxed text-sm md:text-base">
              Vintage Wardrobe Co is a UK-based online retailer specialising in curated vintage and
              streetwear clothing. We are committed to protecting your personal data and respecting
              your privacy in accordance with the UK General Data Protection Regulation (UK GDPR)
              and the Data Protection Act 2018.
            </p>
            <p className="text-softgrey leading-relaxed text-sm md:text-base mt-3">
              For the purposes of data protection law, Vintage Wardrobe Co is the data controller.
              If you have any questions about this policy or how we handle your data, please contact
              us at{' '}
              <a href="mailto:info@vintagewardrobe.co" className="text-[#C9A84C] hover:underline font-semibold">
                info@vintagewardrobe.co
              </a>.
            </p>
          </section>

          {/* What Data We Collect */}
          <section data-testid="data-collected-section">
            <div className="flex items-center gap-3 mb-3">
              <Database className="w-5 h-5 text-[#C9A84C]" />
              <h2 className="text-xl md:text-2xl font-heading font-bold">What Data We Collect</h2>
            </div>
            <p className="text-softgrey leading-relaxed text-sm md:text-base mb-4">
              We collect personal data that you provide to us directly, as well as data collected
              automatically when you use our website. This includes:
            </p>
            <div className="bg-white border border-[#E2E2E2] rounded-xl p-5 md:p-6 space-y-4">
              <div>
                <h3 className="text-sm font-bold text-charcoal mb-2">Information you provide</h3>
                <ul className="space-y-1.5 text-softgrey text-sm">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                    <span><strong>Name</strong> &mdash; to process orders and personalise your experience</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                    <span><strong>Email address</strong> &mdash; for order confirmations, shipping updates, and customer support</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                    <span><strong>Delivery address</strong> &mdash; to ship your orders to the correct location</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                    <span><strong>Phone number</strong> (optional) &mdash; for delivery updates from our couriers</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                    <span><strong>Payment details</strong> &mdash; processed securely by our payment provider (Stripe). We do not store your full card details on our servers.</span>
                  </li>
                </ul>
              </div>
              <div className="border-t border-[#E2E2E2] pt-4">
                <h3 className="text-sm font-bold text-charcoal mb-2">Information collected automatically</h3>
                <ul className="space-y-1.5 text-softgrey text-sm">
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                    <span>Device and browser information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                    <span>IP address and approximate location</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                    <span>Pages visited and browsing behaviour (subject to cookie consent)</span>
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Data */}
          <section data-testid="how-we-use-data-section">
            <div className="flex items-center gap-3 mb-3">
              <Eye className="w-5 h-5 text-[#C9A84C]" />
              <h2 className="text-xl md:text-2xl font-heading font-bold">How We Use Your Data</h2>
            </div>
            <div className="space-y-3">
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-5">
                <h3 className="text-sm font-bold text-charcoal mb-1">Order Processing</h3>
                <p className="text-softgrey text-sm">To fulfil your orders, process payments, arrange delivery, and send order confirmations and shipping updates.</p>
              </div>
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-5">
                <h3 className="text-sm font-bold text-charcoal mb-1">Customer Support</h3>
                <p className="text-softgrey text-sm">To respond to your enquiries, resolve issues, and provide after-sales support including returns and exchanges.</p>
              </div>
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-5">
                <h3 className="text-sm font-bold text-charcoal mb-1">Marketing Communications</h3>
                <p className="text-softgrey text-sm">Only if you have given explicit consent, we may send you promotional emails about new arrivals, sales, and exclusive offers. You can unsubscribe at any time.</p>
              </div>
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-5">
                <h3 className="text-sm font-bold text-charcoal mb-1">Website Improvement</h3>
                <p className="text-softgrey text-sm">To analyse website usage (with your consent) and improve our products, services, and shopping experience.</p>
              </div>
            </div>
          </section>

          {/* Legal Basis */}
          <section data-testid="legal-basis-section">
            <div className="flex items-center gap-3 mb-3">
              <Lock className="w-5 h-5 text-[#C9A84C]" />
              <h2 className="text-xl md:text-2xl font-heading font-bold">Legal Basis for Processing</h2>
            </div>
            <p className="text-softgrey leading-relaxed text-sm md:text-base mb-4">
              Under the UK GDPR, we process your personal data on the following legal bases:
            </p>
            <ul className="space-y-2 text-softgrey text-sm">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                <span><strong>Contract</strong> &mdash; processing necessary to fulfil your order and deliver our services to you.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                <span><strong>Consent</strong> &mdash; for marketing communications and non-essential cookies. You may withdraw consent at any time.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                <span><strong>Legitimate Interest</strong> &mdash; for fraud prevention, website security, and improving our services.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#C9A84C] mt-1.5 flex-shrink-0" />
                <span><strong>Legal Obligation</strong> &mdash; to comply with tax, accounting, and regulatory requirements.</span>
              </li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section data-testid="data-sharing-section">
            <div className="flex items-center gap-3 mb-3">
              <Share2 className="w-5 h-5 text-[#C9A84C]" />
              <h2 className="text-xl md:text-2xl font-heading font-bold">Data Sharing</h2>
            </div>
            <p className="text-softgrey leading-relaxed text-sm md:text-base mb-4">
              We do not sell your personal data. We may share your data with the following trusted
              third parties only as necessary to provide our services:
            </p>
            <div className="bg-white border border-[#E2E2E2] rounded-xl p-5 md:p-6 space-y-3">
              <div>
                <h3 className="text-sm font-bold text-charcoal">Payment Providers</h3>
                <p className="text-softgrey text-sm">Stripe processes your payment information securely. We never store your full card details.</p>
              </div>
              <div className="border-t border-[#E2E2E2] pt-3">
                <h3 className="text-sm font-bold text-charcoal">Shipping Partners</h3>
                <p className="text-softgrey text-sm">Your name and delivery address are shared with our courier partners to fulfil your order.</p>
              </div>
              <div className="border-t border-[#E2E2E2] pt-3">
                <h3 className="text-sm font-bold text-charcoal">Analytics Tools</h3>
                <p className="text-softgrey text-sm">If you consent to analytics cookies, anonymised usage data may be shared with analytics platforms.</p>
              </div>
              <div className="border-t border-[#E2E2E2] pt-3">
                <h3 className="text-sm font-bold text-charcoal">Email Services</h3>
                <p className="text-softgrey text-sm">We use trusted email service providers to send transactional emails such as order confirmations and shipping notifications.</p>
              </div>
            </div>
          </section>

          {/* Data Retention */}
          <section data-testid="data-retention-section">
            <div className="flex items-center gap-3 mb-3">
              <Clock className="w-5 h-5 text-[#C9A84C]" />
              <h2 className="text-xl md:text-2xl font-heading font-bold">Data Retention</h2>
            </div>
            <p className="text-softgrey leading-relaxed text-sm md:text-base">
              We retain your personal data only for as long as necessary to fulfil the purposes for
              which it was collected. Order records are kept for a minimum of 6 years to comply with
              UK tax and accounting regulations. Account data is retained while your account is active.
              If you request account deletion, we will remove your personal data within 30 days,
              except where retention is required by law.
            </p>
          </section>

          {/* Your Rights */}
          <section data-testid="your-rights-section">
            <div className="flex items-center gap-3 mb-3">
              <UserCheck className="w-5 h-5 text-[#C9A84C]" />
              <h2 className="text-xl md:text-2xl font-heading font-bold">Your Rights</h2>
            </div>
            <p className="text-softgrey leading-relaxed text-sm md:text-base mb-4">
              Under the UK GDPR, you have the following rights regarding your personal data:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-4">
                <h3 className="text-sm font-bold text-charcoal mb-1">Right of Access</h3>
                <p className="text-softgrey text-xs leading-relaxed">Request a copy of the personal data we hold about you.</p>
              </div>
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-4">
                <h3 className="text-sm font-bold text-charcoal mb-1">Right to Correction</h3>
                <p className="text-softgrey text-xs leading-relaxed">Request correction of any inaccurate or incomplete data.</p>
              </div>
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-4">
                <h3 className="text-sm font-bold text-charcoal mb-1">Right to Deletion</h3>
                <p className="text-softgrey text-xs leading-relaxed">Request deletion of your personal data where no legal basis for retention exists.</p>
              </div>
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-4">
                <h3 className="text-sm font-bold text-charcoal mb-1">Right to Object</h3>
                <p className="text-softgrey text-xs leading-relaxed">Object to the processing of your data for direct marketing purposes.</p>
              </div>
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-4">
                <h3 className="text-sm font-bold text-charcoal mb-1">Right to Restrict Processing</h3>
                <p className="text-softgrey text-xs leading-relaxed">Request that we limit how we use your personal data.</p>
              </div>
              <div className="bg-white border border-[#E2E2E2] rounded-xl p-4">
                <h3 className="text-sm font-bold text-charcoal mb-1">Right to Data Portability</h3>
                <p className="text-softgrey text-xs leading-relaxed">Receive your data in a structured, commonly used format.</p>
              </div>
            </div>
            <p className="text-softgrey text-sm mt-4">
              To exercise any of these rights, please email{' '}
              <a href="mailto:info@vintagewardrobe.co" className="text-[#C9A84C] hover:underline font-semibold">
                info@vintagewardrobe.co
              </a>.
              We will respond to your request within 30 days. If you are unsatisfied with our response,
              you have the right to lodge a complaint with the{' '}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-[#C9A84C] hover:underline font-semibold">
                Information Commissioner's Office (ICO)
              </a>.
            </p>
          </section>

          {/* Cookies Reference */}
          <section data-testid="cookies-reference-section">
            <h2 className="text-xl md:text-2xl font-heading font-bold mb-3">Cookies</h2>
            <p className="text-softgrey leading-relaxed text-sm md:text-base">
              We use cookies to enhance your browsing experience. For detailed information about the
              cookies we use and how to manage your preferences, please see our{' '}
              <Link to="/cookie-policy" className="text-[#C9A84C] hover:underline font-semibold">
                Cookie Policy
              </Link>.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-white border border-[#E2E2E2] rounded-xl p-6 md:p-8" data-testid="privacy-contact-section">
            <div className="flex items-center gap-3 mb-3">
              <Mail className="w-5 h-5 text-[#C9A84C]" />
              <h2 className="text-xl md:text-2xl font-heading font-bold">Contact Us</h2>
            </div>
            <p className="text-softgrey leading-relaxed text-sm md:text-base mb-3">
              If you have any questions about this Privacy Policy or how we handle your personal data,
              please don't hesitate to get in touch:
            </p>
            <div className="space-y-1 text-sm text-softgrey">
              <p><strong className="text-charcoal">Email:</strong>{' '}
                <a href="mailto:info@vintagewardrobe.co" className="text-[#C9A84C] hover:underline">info@vintagewardrobe.co</a>
              </p>
              <p><strong className="text-charcoal">Contact Page:</strong>{' '}
                <Link to="/contact" className="text-[#C9A84C] hover:underline">vintagewardrobe.co/contact</Link>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
